import type { KeyboardEvent } from 'react';

import { STORE_DONATION_MARKUP_AMOUNT_LIMITS } from '../constants/storeCheckout.constants';
import type { StoreReviewStoreType } from './storeReviewDonationValidation.utils';
import type { IUpdateStoreDonationMarkupRequest } from '@/common/interfaces/store.interface';

export const STORE_DONATION_MARKUP_UPDATE_DEBOUNCE_MS = 500;

const { MIN: STORE_DONATION_MARKUP_MIN_AMOUNT, MAX: STORE_DONATION_MARKUP_MAX_AMOUNT } =
  STORE_DONATION_MARKUP_AMOUNT_LIMITS;

export const preventStoreDonationMarkupNegativeKey = (
  event: KeyboardEvent<HTMLInputElement>
): void => {
  const { key } = event;

  if (['-', '+', 'e', 'E'].includes(key)) {
    event.preventDefault();
  }
};

export const sanitizeStoreDonationMarkupInput = (amount: string): string => {
  if (!amount) {
    return '';
  }

  let sanitized = amount.replaceAll('-', '').replace(/[^0-9.]/g, '');
  const dotParts = sanitized.split('.');

  if (dotParts.length > 2) {
    sanitized = `${dotParts[0]}.${dotParts.slice(1).join('')}`;
  }

  const [integerPart, decimalPart] = sanitized.split('.');

  if (decimalPart !== undefined && decimalPart.length > 2) {
    sanitized = `${integerPart}.${decimalPart.slice(0, 2)}`;
  }

  if (!sanitized || sanitized === '.') {
    return '';
  }

  const parsed = Number.parseFloat(sanitized);

  if (Number.isNaN(parsed)) {
    return '';
  }

  if (parsed > STORE_DONATION_MARKUP_MAX_AMOUNT) {
    return String(STORE_DONATION_MARKUP_MAX_AMOUNT);
  }

  return sanitized;
};

export const normalizeStoreDonationMarkupOnBlur = (amount: string): string => {
  const sanitized = sanitizeStoreDonationMarkupInput(amount);

  if (!sanitized) {
    return '';
  }

  const parsed = Number.parseFloat(sanitized);

  if (Number.isNaN(parsed) || parsed < STORE_DONATION_MARKUP_MIN_AMOUNT) {
    return '';
  }

  return sanitized.endsWith('.') ? sanitized.slice(0, -1) : sanitized;
};

export const formatStoreDonationMarkupAmount = (amount: string): string => {
  const trimmed = amount.trim();

  if (!trimmed) {
    return '0.00';
  }

  const parsed = Number.parseFloat(trimmed);

  if (Number.isNaN(parsed) || parsed < STORE_DONATION_MARKUP_MIN_AMOUNT) {
    return '0.00';
  }

  const clamped = Math.min(parsed, STORE_DONATION_MARKUP_MAX_AMOUNT);

  return clamped.toFixed(2);
};

export const isStoreDonationMarkupStoreType = (
  storeType: StoreReviewStoreType
): storeType is 'fundraiser' | 'brand-drop' =>
  storeType === 'fundraiser' || storeType === 'brand-drop';

export const buildStoreDonationMarkupUpdatePayload = (
  orderId: number,
  orderItemId: number,
  amount: string,
  storeType: 'fundraiser' | 'brand-drop'
): IUpdateStoreDonationMarkupRequest => {
  const formattedAmount = formatStoreDonationMarkupAmount(amount);

  return {
    order_id: orderId,
    order_items: [
      storeType === 'brand-drop'
        ? { id: orderItemId, markup_amount: formattedAmount }
        : { id: orderItemId, donation_amount: formattedAmount },
    ],
  };
};
